import type { PAGINATION_ORDER } from 'shared/constants'
import { CommentsTypeEnum, type CommentSortEnum } from 'modules/comments/types'

export interface PageCommentsParams {
  parentId: number | null
  commentsType: CommentsTypeEnum
  pageId: number
  lastCommentId: number
  sortOption: CommentSortEnum
  targetCommentId?: number
}

export type PageCommentsQueryParams = Omit<PageCommentsParams, 'lastCommentId'>

export interface PinnedCommentsParams {
  lastCommentId: number
  parentId: number | null
  postId: number
  sortOrder: (typeof PAGINATION_ORDER)[keyof typeof PAGINATION_ORDER]
  targetCommentId?: number
}

export const commentsKeys = {
  all: ['comments'] as const,
  resource: (commentsType: CommentsTypeEnum, pageId: number) =>
    [...commentsKeys.all, commentsType, pageId] as const,
  pages: (commentsType: CommentsTypeEnum, pageId: number) =>
    [...commentsKeys.resource(commentsType, pageId), 'page'] as const,
  pageCollection: ({ commentsType, pageId, parentId, sortOption }: PageCommentsQueryParams) =>
    parentId === null
      ? ([...commentsKeys.pages(commentsType, pageId), sortOption] as const)
      : commentsKeys.replies(commentsType, pageId, parentId),
  page: (params: PageCommentsQueryParams) =>
    [...commentsKeys.pageCollection(params), 'list'] as const,
  focus: (
    params: PageCommentsQueryParams & {
      targetCommentId: number
    },
  ) => [...commentsKeys.pageCollection(params), 'focus', params.targetCommentId] as const,
  pinnedPages: (postId: number) =>
    [...commentsKeys.resource(CommentsTypeEnum.Community, postId), 'pinned'] as const,
  pinned: (postId: number) => [...commentsKeys.pinnedPages(postId), 'list'] as const,
  pinnedFocus: (postId: number, targetCommentId: number) =>
    [...commentsKeys.pinnedPages(postId), 'focus', targetCommentId] as const,
  replies: (commentsType: CommentsTypeEnum, pageId: number, parentId: number) =>
    [...commentsKeys.resource(commentsType, pageId), 'replies', parentId] as const,
  add: (commentsType: CommentsTypeEnum, pageId: number) =>
    [...commentsKeys.resource(commentsType, pageId), 'add'] as const,
  toggleLike: (commentsType: CommentsTypeEnum, pageId: number, commentId: number) =>
    [...commentsKeys.resource(commentsType, pageId), 'toggle-like', commentId] as const,
  update: (commentsType: CommentsTypeEnum, pageId: number, commentId: number) =>
    [...commentsKeys.resource(commentsType, pageId), 'update', commentId] as const,
  delete: (commentsType: CommentsTypeEnum, pageId: number) =>
    [...commentsKeys.resource(commentsType, pageId), 'delete'] as const,
  likes: (commentId: number) => [...commentsKeys.all, 'likes', commentId] as const,
}
